import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, TextField, FormControl, InputLabel, Select, MenuItem,
  ToggleButton, ToggleButtonGroup, Tooltip, Card, CardContent, CardActionArea,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Divider,
  IconButton, useTheme, alpha,
} from '@mui/material';
import {
  Search, SortByAlpha, ViewModule, TableRows, Visibility,
  CalendarMonth, Receipt, CheckCircle, HourglassBottom, Cancel,
  Close, FilterList, Menu as MenuIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../state/store';

interface Transaction {
  _id?: string;
  transactionId: string;
  amount: number;
  currency: string;
  status: string;
  createdAt: string;
  planDuration?: string;
  licenseType?: string;
  module?: string;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  gbp: '£', usd: '$', eur: '€', inr: '₹', aud: 'A$', aed: 'د.إ',
};

const STATUS_CONFIG: Record<string, { color: 'success' | 'warning' | 'error'; icon: React.ReactElement }> = {
  success: { color: 'success', icon: <CheckCircle fontSize="small" /> },
  pending: { color: 'warning', icon: <HourglassBottom fontSize="small" /> },
  failed: { color: 'error', icon: <Cancel fontSize="small" /> },
};

const VIEWMODE_KEY = 'payment-history-view-mode';

const formatCurrency = (amount: number, currency: string) => {
  const sym = CURRENCY_SYMBOLS[currency.toLowerCase()] || currency.toUpperCase();
  return `${sym}${(amount / 100).toFixed(2)}`;
};

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const PaymentHistory: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const company = useSelector((state: RootState) => state.authentication.company);

  const transactions: Transaction[] = useMemo(
    () => (company?.transactions as Transaction[]) || [],
    [company],
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [viewMode, setViewMode] = useState<'tile' | 'table'>(() => {
    const stored = localStorage.getItem(VIEWMODE_KEY);
    return stored === 'table' ? 'table' : 'tile';
  });
  const [selectedTx, setSelectedTx] = useState<Transaction | null>(null);
  const [mobileToolbarOpen, setMobileToolbarOpen] = useState(false);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    let list = transactions.filter(tx => {
      if (!term) return true;
      return (
        tx.transactionId?.toLowerCase().includes(term) ||
        tx.status?.toLowerCase().includes(term) ||
        tx.planDuration?.toLowerCase().includes(term) ||
        tx.module?.toLowerCase().includes(term) ||
        tx.licenseType?.toLowerCase().includes(term) ||
        formatCurrency(tx.amount, tx.currency).toLowerCase().includes(term) ||
        formatDate(tx.createdAt).toLowerCase().includes(term)
      );
    });
    list.sort((a, b) => {
      const da = new Date(a.createdAt).getTime();
      const db = new Date(b.createdAt).getTime();
      return sortOrder === 'desc' ? db - da : da - db;
    });
    return list;
  }, [transactions, searchTerm, sortOrder]);

  const statusChip = (status: string) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return (
      <Chip
        icon={cfg.icon}
        label={status.toUpperCase()}
        color={cfg.color}
        size="small"
        variant="filled"
        sx={{ fontWeight: 600, letterSpacing: 0.5 }}
      />
    );
  };

  const renderDetailDialog = () => {
    if (!selectedTx) return null;
    const modules = selectedTx.module ? selectedTx.module.split(',').map(m => m.trim()).filter(Boolean) : [];
    return (
      <Dialog open maxWidth="sm" fullWidth onClose={() => setSelectedTx(null)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Receipt color="secondary" />
            <Typography variant="h6" fontWeight={600}>Transaction Details</Typography>
          </Stack>
          <IconButton size="small" onClick={() => setSelectedTx(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2.5}>
            <DetailRow label="Payment Date" value={formatDate(selectedTx.createdAt)} />
            <DetailRow label="Amount" value={formatCurrency(selectedTx.amount, selectedTx.currency)} />
            <DetailRow label="Transaction ID" value={selectedTx.transactionId} mono />
            <DetailRow label="Status" valueNode={statusChip(selectedTx.status)} />
            <DetailRow label="Plan Duration" value={selectedTx.planDuration || '—'} />
            {modules.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                  Licensed Modules
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {modules.map(m => (
                    <Chip key={m} label={m} size="small" variant="outlined" color="secondary" />
                  ))}
                </Stack>
              </Box>
            )}
            <DetailRow label="License Type" value={selectedTx.licenseType || '—'} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSelectedTx(null)} variant="outlined" color="secondary">Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderTile = (tx: Transaction) => (
    <Card
      key={tx._id || tx.transactionId}
      sx={{
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        transition: 'box-shadow 0.2s, transform 0.15s',
        '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.22)', transform: 'translateY(-2px)' },
      }}
    >
      <CardActionArea onClick={() => setSelectedTx(tx)} sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarMonth fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={500}>{formatDate(tx.createdAt)}</Typography>
          </Stack>
          {statusChip(tx.status)}
        </Stack>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
          {formatCurrency(tx.amount, tx.currency)}
        </Typography>
        {tx.planDuration && (
          <Typography variant="body2" color="text.secondary">
            {tx.planDuration}
          </Typography>
        )}
      </CardActionArea>
    </Card>
  );

  const renderTable = () => (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: isDark ? alpha(theme.palette.divider, 0.15) : 'divider',
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: isDark ? alpha('#fff', 0.05) : alpha('#000', 0.04) }}>
            <TableCell sx={{ fontWeight: 700 }}>Payment Date</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
            <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Transaction ID</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700, display: { xs: 'none', sm: 'table-cell' } }}>Plan Duration</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                <Typography color="text.secondary">No transactions found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filtered.map(tx => (
              <TableRow
                key={tx._id || tx.transactionId}
                hover
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell>{formatDate(tx.createdAt)}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(tx.amount, tx.currency)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', display: { xs: 'none', md: 'table-cell' } }}>
                  {tx.transactionId}
                </TableCell>
                <TableCell>{statusChip(tx.status)}</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{tx.planDuration || '—'}</TableCell>
                <TableCell align="center">
                  <Tooltip title="View Details">
                    <IconButton size="small" color="secondary" onClick={() => setSelectedTx(tx)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const toolbarControls = (
    <>
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Sort Order</InputLabel>
        <Select
          value={sortOrder}
          label="Sort Order"
          onChange={e => setSortOrder(e.target.value as 'asc' | 'desc')}
        >
          <MenuItem value="desc">Newest First</MenuItem>
          <MenuItem value="asc">Oldest First</MenuItem>
        </Select>
      </FormControl>
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={(_e, v) => { if (v) { setViewMode(v); localStorage.setItem(VIEWMODE_KEY, v); } }}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            border: '1px solid',
            borderColor: 'divider',
            '&.Mui-selected': {
              backgroundColor: (t) => t.palette.toggle?.selectedBg ?? t.palette.secondary.main,
              color: (t) => t.palette.toggle?.selectedColor ?? t.palette.secondary.contrastText,
              '&:hover': { backgroundColor: (t) => t.palette.toggle?.selectedHoverBg ?? t.palette.secondary.dark },
            },
          },
        }}
      >
        <ToggleButton value="tile" aria-label="Tile view">
          <Tooltip title="Tile View"><ViewModule fontSize="small" /></Tooltip>
        </ToggleButton>
        <ToggleButton value="table" aria-label="Table view">
          <Tooltip title="Table View"><TableRows fontSize="small" /></Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
    </>
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Typography variant="h4" fontWeight={600} gutterBottom sx={{ fontSize: { xs: '1.4rem', sm: '2rem' } }}>
        Payment History
      </Typography>

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: { xs: 1, sm: 2 },
          alignItems: 'center',
          mb: 3,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: isDark ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
        }}
      >
        {/* Mobile: menu button */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, ml: 'auto' }}>
          <IconButton
            color="secondary"
            onClick={() => setMobileToolbarOpen(true)}
            sx={{ border: '1px solid', borderColor: 'divider' }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Desktop: search */}
        <Box sx={{ display: { xs: 'none', sm: 'block' }, flexGrow: 1 }}>
          <TextField
            label="Search Transactions"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> }}
            sx={{ minWidth: 220 }}
          />
        </Box>

        {/* Desktop: sort + view toggle */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1, alignItems: 'center' }}>
          {toolbarControls}
        </Box>
      </Box>

      {/* Mobile Toolbar Dialog */}
      <Dialog open={mobileToolbarOpen} onClose={() => setMobileToolbarOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FilterList />
            <Typography fontWeight={600}>Search & Sort</Typography>
          </Stack>
          <IconButton size="small" onClick={() => setMobileToolbarOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Search Transactions"
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {toolbarControls}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMobileToolbarOpen(false)} variant="outlined" color="secondary">Done</Button>
        </DialogActions>
      </Dialog>

      {/* Content */}
      {transactions.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', minHeight: 300, border: '1px dashed', borderColor: 'divider', borderRadius: 2,
          }}
        >
          <Receipt sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No Payment History</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Transaction records will appear here once payments are made.
          </Typography>
        </Paper>
      ) : viewMode === 'tile' ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {filtered.length === 0 ? (
            <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">No transactions match your search</Typography>
            </Box>
          ) : (
            filtered.map(tx => renderTile(tx))
          )}
        </Box>
      ) : (
        renderTable()
      )}

      {renderDetailDialog()}
    </Box>
  );
};

const DetailRow: React.FC<{
  label: string;
  value?: string;
  valueNode?: React.ReactNode;
  mono?: boolean;
}> = ({ label, value, valueNode, mono }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.25, display: 'block' }}>
      {label}
    </Typography>
    {valueNode || (
      <Typography
        variant="body1"
        fontWeight={500}
        sx={mono ? { fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' } : {}}
      >
        {value}
      </Typography>
    )}
  </Box>
);
